/**
 * Paean API Client
 * HTTP client for communicating with the Paean AI backend
 */

import axios, { type AxiosInstance, type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { getToken, getApiUrl, clearAuth, getRefreshToken, isTokenNearExpiry, storeAuth } from '../utils/config.js';
import { CLI_VERSION } from '../version.js';

const MAX_RETRIES = 3;
const RETRY_BASE_DELAY_MS = 1000;
const RETRYABLE_STATUS_CODES = new Set([408, 429, 500, 502, 503, 504]);

function isRetryableError(error: AxiosError): boolean {
  if (!error.response) {
    return true;
  }
  return RETRYABLE_STATUS_CODES.has(error.response.status);
}

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

const cliVersion = CLI_VERSION;

export interface ApiError {
  error: string;
  message?: string;
  statusCode?: number;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

/**
 * Create an authenticated API client
 * @param options - Configuration options
 * @param options.clearAuthOn401 - Whether to clear auth on 401 errors (default: true)
 */
export function createApiClient(options?: { clearAuthOn401?: boolean }): AxiosInstance {
  const clearAuthOn401 = options?.clearAuthOn401 !== false; // Default to true for backward compatibility
  
  const client = axios.create({
    baseURL: getApiUrl(),
    timeout: 30000,
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'User-Agent': `Paean-CLI/${cliVersion}`,
    },
  });

  // Add auth token to requests, with proactive refresh
  client.interceptors.request.use(async (config) => {
    // Proactively refresh token if near expiry
    if (isTokenNearExpiry()) {
      await refreshAuthToken();
    }
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  // Handle response errors with token refresh on 401 and retry on transient errors
  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError<ApiError>) => {
      // On 401, attempt token refresh before giving up
      if (error.response?.status === 401 && clearAuthOn401) {
        const refreshToken = getRefreshToken();
        if (refreshToken && error.config && !(error.config as unknown as Record<string, unknown>)._retried) {
          const refreshed = await refreshAuthToken();
          if (refreshed) {
            const config = error.config;
            (config as unknown as Record<string, unknown>)._retried = true;
            const token = getToken();
            if (token) {
              config.headers.Authorization = `Bearer ${token}`;
            }
            return client.request(config);
          }
        }
        clearAuth();
      }

      // Retry on transient errors with exponential backoff
      if (error.config && isRetryableError(error)) {
        const config = error.config as InternalAxiosRequestConfig & { _retryCount?: number };
        config._retryCount = (config._retryCount || 0) + 1;

        if (config._retryCount <= MAX_RETRIES) {
          const delay = RETRY_BASE_DELAY_MS * Math.pow(2, config._retryCount - 1);
          await sleep(delay);
          return client.request(config);
        }
      }

      // Extract error message — handle both { error: "string" } and { error: { message } } formats
      const rawError = error.response?.data?.error;
      let errorMessage: string;
      if (typeof rawError === 'string') {
        errorMessage = rawError;
      } else if (rawError && typeof rawError === 'object' && typeof (rawError as any).message === 'string') {
        errorMessage = (rawError as any).message;
      } else {
        errorMessage = error.response?.data?.message || error.message || 'An unknown error occurred';
      }

      const enhancedError = new Error(errorMessage) as Error & {
        statusCode?: number;
        isApiError: boolean;
      };
      enhancedError.statusCode = error.response?.status;
      enhancedError.isApiError = true;

      return Promise.reject(enhancedError);
    }
  );

  return client;
}

/**
 * Create an unauthenticated API client (for login flows)
 */
export function createPublicApiClient(): AxiosInstance {
  const client = axios.create({
    baseURL: getApiUrl(),
    timeout: 30000,
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'User-Agent': `Paean-CLI/${cliVersion}`,
    },
  });

  // Handle response errors
  client.interceptors.response.use(
    (response) => response,
    (error: AxiosError<ApiError>) => {
      const rawError = error.response?.data?.error;
      let errorMessage: string;
      if (typeof rawError === 'string') {
        errorMessage = rawError;
      } else if (rawError && typeof rawError === 'object' && typeof (rawError as any).message === 'string') {
        errorMessage = (rawError as any).message;
      } else {
        errorMessage = error.response?.data?.message || error.message || 'An unknown error occurred';
      }

      const enhancedError = new Error(errorMessage) as Error & {
        statusCode?: number;
        isApiError: boolean;
      };
      enhancedError.statusCode = error.response?.status;
      enhancedError.isApiError = true;

      return Promise.reject(enhancedError);
    }
  );

  return client;
}

/**
 * Refresh the authentication token using the refresh token
 * Returns true if refresh was successful
 */
let isRefreshing = false;
let refreshPromise: Promise<boolean> | null = null;

async function refreshAuthToken(): Promise<boolean> {
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  // zero-api's /auth/refresh-token uses checkJWT middleware,
  // so we must send the current (near-expiry) token in the Authorization header.
  const currentToken = getToken();
  if (!currentToken) return false;

  isRefreshing = true;
  refreshPromise = (async () => {
    try {
      const response = await axios.post(`${getApiUrl()}/auth/refresh-token`, {}, {
        headers: {
          'Content-Type': 'application/json',
          'User-Agent': `Paean-CLI/${cliVersion}`,
          'Authorization': `Bearer ${currentToken}`,
        },
        timeout: 10000,
      });

      const data = response.data as {
        token?: string;
        refreshToken?: string;
        expiresAt?: string;
      };

      if (data.token) {
        storeAuth({
          token: data.token,
          refreshToken: data.refreshToken,
          expiresAt: data.expiresAt,
        });
        return true;
      }
      return false;
    } catch {
      return false;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

// Singleton instances
let apiClient: AxiosInstance | null = null;
let publicApiClient: AxiosInstance | null = null;
let nonClearingApiClient: AxiosInstance | null = null;

/**
 * Get the authenticated API client singleton
 * @param options - Configuration options
 */
export function getApiClient(options?: { clearAuthOn401?: boolean }): AxiosInstance {
  if (!apiClient) {
    apiClient = createApiClient(options);
  }
  return apiClient;
}

/**
 * Create a temporary API client that doesn't clear auth on 401
 * Useful for cleanup operations where 401 errors shouldn't invalidate the session
 */
export function createNonClearingApiClient(): AxiosInstance {
  if (!nonClearingApiClient) {
    nonClearingApiClient = createApiClient({ clearAuthOn401: false });
  }
  return nonClearingApiClient;
}

/**
 * Get the public API client singleton
 */
export function getPublicApiClient(): AxiosInstance {
  if (!publicApiClient) {
    publicApiClient = createPublicApiClient();
  }
  return publicApiClient;
}

/**
 * Reset the API clients (useful after login/logout)
 */
export function resetApiClients(): void {
  apiClient = null;
  publicApiClient = null;
  nonClearingApiClient = null;
}
